import { buildDownloadUrl, safeDownload } from "@/common/util/downloadCore";
import i18n from "@/i18n";
import router from "@/router/index";
import { MutableRequired } from "@medapro-types/common/common";
import { ProgressHandler } from "@medapro-types/common/components/ProgressHandling";
import {
  TranslationManagerActions,
  TranslationManagerGetters,
  TranslationManagerMutations,
  TranslationManagerState
} from "@medapro-types/TranslationManager/translation-manager-store";
import {
  Catalog,
  CreateTranslationProjectData,
  DEFAULT_PAGE_SIZE,
  SearchParameterState,
  TranslationDetailEntry,
  TranslationDetailSearchParameters,
  TranslationEntryValueFilterType,
  TranslationFileType,
  TranslationGroup,
  TranslationGroupEntry,
  TranslationGroupSearchParameters,
  TranslationGroupType,
  TranslationManagerOverviewValues,
  TranslationProject
} from "@medapro-types/TranslationManager/translationManager";
import eventLogger from "@services/ApiLogger/EventLogger.js";
import catalogApi from "@services/catalogs.js";
import translationApi from "@services/deepLTranslation.js";
import endpoints from "@services/endpoints";
import translationManagerApi, { TranslationProjectCreateCriteria } from "@services/translationManager";
import { getUnchangedTranslationGroupEntry } from "@TranslationManager/util/translationGroup";
import { enumValues } from "@util/enums/enum-util";
import { CanceledError } from "axios";
import _ from "lodash";
import { sha1 as hash } from "object-hash";
import emitter from "tiny-emitter/instance";

const { t: $t } = i18n.global;

const defaultState = (): TranslationManagerState => ({
  runningPromises: [],
  saveMessageSeen: true,
  translationGroups: [],
  translationProjects: [],
  currentTranslationProject: null,
  errors: {
    projectError: null,
    groupErrors: {
      [TranslationGroupType.ArticleDescriptions]: null,
      [TranslationGroupType.ArticleNames]: null,
      [TranslationGroupType.AttributeValues]: null,
      [TranslationGroupType.AttributeUnits]: null,
      [TranslationGroupType.CatalogGroupAttributes]: null,
      [TranslationGroupType.CatalogGroupDescriptions]: null,
      [TranslationGroupType.CatalogGroupNames]: null,
      [TranslationGroupType.GlobalAttributeUnits]: null,
      [TranslationGroupType.GlobalAttributeValues]: null,
      [TranslationGroupType.Keywords]: null,
      [TranslationGroupType.VariationGroups]: null
    }
  },
  translationProjectHashes: new Map<string, string>(),
  catalogs: [],
  isSaving: false,
  isLoading: false,
  overviewValues: {
    textCount: 0,
    emptyTextCount: 0,
    translatedTextCount: 0,
    wordCount: 0,
    emptyWordCount: 0,
    translatedWordCount: 0,
    characterCount: 0,
    emptyCharacterCount: 0,
    translatedCharacterCount: 0
  },
  isOverviewLoading: false,
  isCreatingProject: false
});

const getters: TranslationManagerGetters = {
  hasUnsavedData: function (state: TranslationManagerState) {
    const groupWithChanges = state.translationGroups.filter((groupEntry) => {
      const currentHash = hash(groupEntry.entries);
      if (groupEntry.initialHash !== currentHash) {
        return true;
      }

      return false;
    });
    const projectsWithChanges = state.translationProjects.filter(
      (project) =>
        !state.translationProjectHashes.has(project.translationProjectId) || state.translationProjectHashes.get(project.translationProjectId) !== hash(project)
    );
    return groupWithChanges.length || projectsWithChanges.length;
  },
  isBusy: function (state: TranslationManagerState) {
    return !!state.runningPromises.length;
  },
  lastRunSuccess: function (state: TranslationManagerState) {
    if (state.errors.projectError) {
      return false;
    }

    const values = Object.values(state.errors.groupErrors);
    return values.every((groupError) => !groupError);
  },
  isGroupLoading: function (state: TranslationManagerState) {
    return (groupType: TranslationGroupType | null) => {
      if (!groupType) {
        return false;
      }

      return state.translationGroups.find((group) => group.type === groupType)?.searchParameters?.isLoading;
    };
  },
  hasGroupUnsavedData: function (state: TranslationManagerState) {
    return (groupType: TranslationGroupType | null) => {
      if (!groupType) {
        return false;
      }

      const translationGroup = state.translationGroups.find((group) => group.type === groupType);
      if (!translationGroup) {
        return false;
      }

      return translationGroup.initialHash !== hash(translationGroup.entries);
    };
  }
};

const mutations: TranslationManagerMutations = {
  "reset-state"(state) {
    Object.assign(state, defaultState());
  },
  "notification-seen": function (state: TranslationManagerState) {
    state.saveMessageSeen = true;
  },
  "notification-show": function (state: TranslationManagerState) {
    state.saveMessageSeen = false;
  },
  "add-running-promise": function (state: TranslationManagerState, args: Promise<any>) {
    state.runningPromises = [...state.runningPromises, args];
  },
  "reset-running-promises": function (state: TranslationManagerState) {
    state.runningPromises = [];
  },
  "set-translation-projects": function (state: TranslationManagerState, args: TranslationProject[]) {
    state.translationProjects = args;
  },
  "set-translation-groups": function (state: TranslationManagerState, args: TranslationGroup[]) {
    state.translationGroups = args;
  },
  "set-translation-group-entries": function (state, args: { groupType: TranslationGroupType; entries: TranslationGroupEntry[] }) {
    state.translationGroups = state.translationGroups.map((entry) => {
      if (entry.type == args.groupType) {
        entry.entries = args.entries;
      }
      return entry;
    });
  },
  "set-translation-detail-entries": function (state, args: { groupType: TranslationGroupType; sourceText: string; detailEntries: TranslationDetailEntry[] }) {
    state.translationGroups = state.translationGroups.map((group) => {
      if (group.type == args.groupType) {
        group.entries.map((entry) => {
          if (entry.sourceText == args.sourceText) {
            entry.detailEntries = args.detailEntries;
          }
        });
      }
      return group;
    });
  },
  "set-current-translation-project": function (state: TranslationManagerState, args: TranslationProject | null) {
    state.currentTranslationProject = args;
  },
  "set-translation-project-error": function (state: TranslationManagerState, args: Error | null) {
    state.errors.projectError = args;
  },
  "set-translation-group-error": function (state: TranslationManagerState, args: { type: TranslationGroupType; error: Error | null }) {
    state.errors.groupErrors[args.type] = args.error;
  },
  "reset-errors": function (state) {
    state.errors.projectError = null;
    const groupKeys = Object.keys(state.errors.groupErrors);
    for (const groupKey in groupKeys) {
      state.errors.groupErrors[groupKey as TranslationGroupType] = null;
    }
  },
  "set-translation-group-hash": function (state: TranslationManagerState, args: { groupType: TranslationGroupType; hash: string }) {
    const translationGroup = state.translationGroups.find((entry) => entry.type === args.groupType);
    if (translationGroup) {
      translationGroup.initialHash = args.hash;
    }
  },
  "set-translation-project-name": function (state: TranslationManagerState, args: { translationProjectId: string; name: string }) {
    state.translationProjects = state.translationProjects.map((project) => {
      if (project.translationProjectId === args.translationProjectId) {
        const translationProject = _.cloneDeep(project) as MutableRequired<TranslationProject>;
        translationProject.name = args.name;

        if (state.currentTranslationProject && state.currentTranslationProject.translationProjectId === args.translationProjectId) {
          state.currentTranslationProject = translationProject;
        }

        return translationProject;
      }

      return project;
    });
  },
  "set-translation-project-hash": function (state: TranslationManagerState, args: { project: TranslationProject; hash: string }) {
    state.translationProjectHashes.set(args.project.translationProjectId, args.hash);
  },
  "set-catalogs": function (state: TranslationManagerState, args: Catalog[]) {
    state.catalogs = args;
  },
  "set-translation-group-search-parameters": function (
    state: TranslationManagerState,
    args: { groupType: TranslationGroupType; searchParameters: SearchParameterState }
  ) {
    const translationGroup = state.translationGroups.find((entry) => entry.type === args.groupType);
    if (translationGroup) {
      translationGroup.searchParameters = args.searchParameters;
    }
  },
  "set-translation-group-loading": function (state: TranslationManagerState, args: { groupType: TranslationGroupType; newValue: boolean }) {
    const translationGroup = state.translationGroups.find((entry) => entry.type === args.groupType);
    if (translationGroup && translationGroup.searchParameters) {
      translationGroup.searchParameters.isLoading = args.newValue;
    }
  },
  "set-is-loading": function (state: TranslationManagerState, args: boolean) {
    state.isLoading = args;
  },
  "set-is-saving": function (state: TranslationManagerState, args: boolean) {
    state.isSaving = args;
  },
  "set-is-overview-loading": function (state: TranslationManagerState, args: boolean) {
    state.isOverviewLoading = args;
  },
  "set-is-creating-project": function (state: TranslationManagerState, args: boolean) {
    state.isCreatingProject = args;
  },
  "set-overview-values": function (state: TranslationManagerState, args: TranslationManagerOverviewValues) {
    state.overviewValues = args;
  }
};

const actions: TranslationManagerActions = {
  resetRunningPromisesIfResolved: async function ({ state, commit }) {
    await Promise.all(state.runningPromises);
    commit("reset-running-promises");
  },
  loadTranslationProject: async function ({ state, commit, dispatch }, args: TranslationProject | null) {
    const logger = eventLogger.beginContext("translationManager.loadProject");
    try {
      if (!args) {
        logger.info("Resetting translation project data. No project was selected").alsoToConsole();
        commit("set-translation-groups", []);
        commit("set-current-translation-project", null);
        return;
      }

      commit("set-is-loading", true);
      logger.info("Loading translation project data from API").alsoToConsole();
      const translationProject = _.cloneDeep(args) as MutableRequired<TranslationProject>;
      if (!translationProject.groups?.length) {
        const groupCounts = await translationManagerApi.getGroupCounts(translationProject.translationProjectId);
        translationProject.groups = [
          { type: TranslationGroupType.ArticleDescriptions, count: groupCounts.articleDescriptionCount ?? 0 },
          { type: TranslationGroupType.ArticleNames, count: groupCounts.articleNameCount ?? 0 },
          { type: TranslationGroupType.AttributeUnits, count: groupCounts.attributeUnitCount ?? 0 },
          { type: TranslationGroupType.AttributeValues, count: groupCounts.attributeValueCount ?? 0 },
          { type: TranslationGroupType.CatalogGroupAttributes, count: groupCounts.catalogNodeAttributeCount ?? 0 },
          { type: TranslationGroupType.CatalogGroupDescriptions, count: groupCounts.catalogNodeDescriptionCount ?? 0 },
          { type: TranslationGroupType.CatalogGroupNames, count: groupCounts.catalogNodeCount ?? 0 },
          { type: TranslationGroupType.GlobalAttributeUnits, count: groupCounts.globalAttributeUnitCount ?? 0 },
          { type: TranslationGroupType.GlobalAttributeValues, count: groupCounts.globalArticleAttributeCount ?? 0 },
          { type: TranslationGroupType.Keywords, count: groupCounts.keywordCount ?? 0 },
          { type: TranslationGroupType.VariationGroups, count: groupCounts.variationGroupCount ?? 0 }
        ].filter((group) => group.count);

        const translationProjects = state.translationProjects.map((project) => {
          if (project.translationProjectId === translationProject.translationProjectId) {
            return translationProject;
          }

          return project;
        });

        commit("set-translation-projects", translationProjects);
      }

      commit("set-current-translation-project", translationProject);
      commit("set-translation-project-hash", { project: translationProject, hash: hash(translationProject) });
      logger.info("Loading translation groups of translation project").alsoToConsole();

      const translationGroups = translationProject.groups.map((group) => {
        const translationGroup = {
          type: group.type,
          entries: [],
          searchParameters: {
            currentPage: 1,
            entriesPerPage: DEFAULT_PAGE_SIZE,
            hasNextPage: false,
            hasPreviousPage: false,
            entryValueFilterType: TranslationEntryValueFilterType.All,
            searchText: null,
            orderByColumn: null,
            orderDirection: false
          },
          count: group.count
        };
        return translationGroup;
      });

      commit("set-translation-groups", translationGroups);
      for (const group of translationGroups) {
        const groupHash = hash(group.entries);
        commit("set-translation-group-hash", { groupType: group.type, hash: groupHash });
        const searchParameters: TranslationGroupSearchParameters = {
          translationProjectId: args.translationProjectId,
          groupType: group.type,
          searchText: group.searchParameters.searchText,
          entryValueFilterType: group.searchParameters.entryValueFilterType,
          pageNumber: group.searchParameters.currentPage,
          entriesPerPage: group.searchParameters.entriesPerPage,
          orderByColumn: group.searchParameters.orderByColumn,
          orderDirection: group.searchParameters.orderDirection
        };
        dispatch("applySearch", searchParameters);
      }

      dispatch("reloadOverviewValues");
      logger.info("Finished loading translation project").alsoToConsole();
    } catch (error) {
      logger.error("Loading translation project failed", { translationProject: args, error: error }).alsoToConsole();
      emitter.emit("global-error", { type: "api", exception: error });
    } finally {
      await dispatch("resetRunningPromisesIfResolved");
      logger.complete();
      commit("set-is-loading", false);
      commit("set-is-creating-project", false);
    }
  },
  loadTranslationProjects: async function ({ commit, state, rootState }) {
    const logger = eventLogger.beginContext("translationManager.loadProjects");
    try {
      logger.info("Starting fetch of all translation projects...").alsoToConsole();
      const projects = await translationManagerApi.getAll();
      logger.info("Finished fetching all translation projects. Beginning to remap required data").alsoToConsole();
      const translationProjects: TranslationProject[] = projects.map((project) => {
        const translationProjectCatalog = state.catalogs.find((catalog) => catalog.catalogId === project.catalogId);
        if (!translationProjectCatalog) {
          throw new Error("Catalog with id " + project.catalogId + " is unknown");
        }

        const languages = rootState.metadata?.contentLanguages.available ?? [];

        const sourceLanguage = languages.find((lang) => project.sourceLanguageId === lang.languageId) ?? null;
        const targetLanguage = languages.find((lang) => project.targetLanguageId === lang.languageId) ?? null;

        const translationProject: TranslationProject = {
          translationProjectId: project.translationProjectId,
          name: project.name,
          catalog: translationProjectCatalog,
          sourceLanguage: sourceLanguage,
          targetLanguage: targetLanguage,
          createdOn: project.createdOn,
          createdBy: project.createdBy,
          updatedOn: project.updatedOn,
          updatedBy: project.updatedBy,
          groups: []
        };

        return translationProject;
      });

      commit("set-translation-projects", translationProjects);
      for (const translationProject of translationProjects) {
        commit("set-translation-project-hash", { project: translationProject, hash: hash(translationProject) });
      }
      logger.info("Finished load of all translation projects").alsoToConsole();
    } catch (error) {
      logger.error("Loading translation projects completed with errors", { exception: error }).alsoToConsole();
      emitter.emit("global-error", { type: "api", exception: error });
    } finally {
      logger.complete();
    }
  },
  loadDetailEntries: async function ({ state, commit }, args: TranslationDetailSearchParameters) {
    const logger = eventLogger.beginContext("translationManager.loadProjects");
    try {
      logger.info("Starting fetch of detail entries...").alsoToConsole();
      const translationGroup = state.translationGroups.find((group) => group.type === args.groupType);
      if (!translationGroup) {
        return;
      }

      const currentDetailEntries = translationGroup.entries.find((entry) => entry.sourceText === args.sourceText)?.detailEntries;
      if (currentDetailEntries && currentDetailEntries.length > 0) {
        return;
      }

      const apiResult = await translationManagerApi.getTranslationDetailEntries(args);
      const apiEntries = Array.from(apiResult);
      const detailEntries = apiEntries.map(function (entry) {
        return {
          referenceName: entry.referenceName,
          referenceNo: entry.referenceNo,
          referenceId: entry.referenceId,
          attributeId: entry.attributeId,
          sourceText: entry.sourceText,
          targetText: entry.targetText,
          originalTargetText: entry.targetText
        };
      });

      commit("set-translation-detail-entries", { groupType: args.groupType, sourceText: args.sourceText, detailEntries: detailEntries });

      if (translationGroup) {
        const unchangedEntries = translationGroup.entries.map((entry) => getUnchangedTranslationGroupEntry(entry));
        const newHash = hash(unchangedEntries);
        commit("set-translation-group-hash", { groupType: translationGroup.type, hash: newHash });
      }
    } catch (error) {
      logger.error("Loading detail entries completed with errors", { exception: error }).alsoToConsole();
      emitter.emit("global-error", { type: "api", exception: error });
    } finally {
      logger.complete();
    }
  },
  setTranslationGroupEntries: function ({ commit }, args: { groupType: TranslationGroupType; entries: TranslationGroupEntry[] }) {
    commit("set-translation-group-entries", args);
  },
  saveAll: async function ({ state, commit, dispatch }) {
    const logger = eventLogger.beginContext("translationManager.saveAll");
    try {
      logger.info("Starting to save translation manager changes...").alsoToConsole();
      commit("set-is-saving", true);
      commit("reset-errors");
      commit("notification-show");

      for (const groupType of enumValues(TranslationGroupType)) {
        commit("add-running-promise", dispatch("saveTranslationGroup", groupType));
      }

      for (const translationProject of state.translationProjects) {
        commit("add-running-promise", dispatch("saveTranslationProject", translationProject));
      }

      logger.info("Finished saving translation manager changes...").alsoToConsole();
    } catch (error) {
      logger.error("Saving translation manager changes failed", { errror: error }).alsoToConsole();
      emitter.emit("global-error", { type: "api", exception: error });
    } finally {
      await dispatch("resetRunningPromisesIfResolved");
      dispatch("reloadOverviewValues");
      logger.complete();
      commit("set-is-saving", false);
    }
  },
  saveTranslationGroup: async function ({ state, commit, dispatch }, args: TranslationGroupType) {
    const logger = eventLogger.beginContext(`translationManager.saveGroup.${args}`);
    try {
      const translationGroup = state.translationGroups.find((entry) => entry.type === args);
      if (!state.currentTranslationProject || !translationGroup || translationGroup.initialHash === hash(translationGroup.entries)) {
        logger.info("Skipping save of translation group").alsoToConsole();
        return;
      }

      logger.info("Starting to save translation group...").alsoToConsole();
      const entriesToUpdate = translationGroup.entries
        .filter((entry) => entry.targetText !== entry.originalTargetText && !entry.hasDifferingValues)
        .map((entry) => {
          return {
            sourceText: entry.sourceText,
            targetText: entry.targetText ?? ""
          };
        });

      await translationManagerApi.updateTranslationEntries(state.currentTranslationProject.translationProjectId, translationGroup.type, entriesToUpdate);
      const detailEntriesToUpdate = translationGroup.entries
        .filter((entry) => entry.hasDifferingValues && entry.detailEntries?.length > 0)
        .flatMap((entry) =>
          entry.detailEntries
            .filter((detailEntry) => detailEntry.originalTargetText !== detailEntry.targetText)
            .map((detailEntry) => {
              return {
                referenceId: detailEntry.referenceId,
                attributeId: detailEntry.attributeId,
                sourceText: detailEntry.sourceText,
                targetText: detailEntry.targetText
              };
            })
        );

      if (detailEntriesToUpdate.length) {
        await translationManagerApi.updateTranslationEntryDetails(
          state.currentTranslationProject.translationProjectId,
          translationGroup.type,
          detailEntriesToUpdate
        );
      }

      commit("set-translation-group-hash", { groupType: translationGroup.type, hash: hash(translationGroup.entries) });
      logger.info("Finished saving translation group.").alsoToConsole();

      if (translationGroup.searchParameters) {
        const searchParameters: TranslationGroupSearchParameters = {
          translationProjectId: state.currentTranslationProject.translationProjectId,
          groupType: args,
          searchText: translationGroup.searchParameters.searchText,
          entryValueFilterType: translationGroup.searchParameters.entryValueFilterType,
          pageNumber: 1,
          entriesPerPage: translationGroup.searchParameters.entriesPerPage,
          orderByColumn: translationGroup.searchParameters.orderByColumn,
          orderDirection: translationGroup.searchParameters.orderDescending
        };

        await dispatch("applySearch", searchParameters);
        logger.info("Finished reload of translation group").alsoToConsole();
      }
    } catch (error) {
      commit("set-translation-group-error", { groupType: args, error: error });
    } finally {
      logger.complete();
    }
  },
  saveTranslationProject: async function ({ state, commit }, translationProject: TranslationProject) {
    const logger = eventLogger.beginContext(`translationManager.saveProject.${translationProject.translationProjectId}`);
    try {
      if (state.translationProjectHashes.get(translationProject.translationProjectId) === hash(translationProject)) {
        logger.info("Skipping save of translation project due to no changes").alsoToConsole();
        return;
      }

      logger.info("Starting to save translation project...").alsoToConsole();
      await translationManagerApi.update({
        translationProjectId: translationProject.translationProjectId,
        name: translationProject.name
      });
      commit("set-translation-project-hash", { project: translationProject, hash: hash(translationProject) });
      logger.info("Finished saving translation project").alsoToConsole();
    } catch (error) {
      logger.error("Saving translation project failed", { translationProject, error }).alsoToConsole();
      commit("set-translation-project-error", error);
    } finally {
      logger.complete();
    }
  },
  renameTranslationProject: function ({ commit }, args: { translationProjectId: string; name: string }) {
    commit("set-translation-project-name", args);
  },
  deleteTranslationProject: async function ({ state, commit }, translationProject: TranslationProject) {
    const logger = eventLogger.beginContext(`translationManager.deleteProject.${translationProject.translationProjectId}`);
    commit("set-is-loading", true);
    try {
      logger.info("Starting deletion of translation project").alsoToConsole();
      await translationManagerApi.delete(translationProject.translationProjectId);
      logger.info("Finished api deletion of translation project");
      const filteredTranslationProjects = state.translationProjects.filter(
        (project) => project.translationProjectId !== translationProject.translationProjectId
      );
      commit("set-translation-projects", filteredTranslationProjects);

      logger.info("Succeessfully filtered local list of translation projects");
      if (state.currentTranslationProject && state.currentTranslationProject.translationProjectId == translationProject.translationProjectId) {
        commit("set-current-translation-project", null);
        if (state.translationProjects.length) {
          const routedProjectId = state.translationProjects[0].translationProjectId;
          logger.info(`Routing to project ${routedProjectId}`).alsoToConsole();
          router.replace({
            name: "translation-manager",
            params: {
              translationProjectId: routedProjectId
            }
          });
        } else {
          logger.info("No more translation project are available. Routing to blank translation manager");
          router.replace({
            name: "translation-manager"
          });
        }
      }
    } catch (error) {
      logger.error(error);
      emitter.emit("global-error", { type: "api", exception: error });
    } finally {
      commit("set-is-loading", false);
      logger.complete();
    }
  },
  loadAll: async function ({ commit, dispatch }) {
    commit("set-is-loading", true);
    try {
      await dispatch("loadCatalogs");
      commit("add-running-promise", dispatch("loadTranslationProjects"));
    } finally {
      await dispatch("resetRunningPromisesIfResolved");
      commit("set-is-loading", false);
    }
  },
  loadCatalogs: async function ({ commit, rootState }) {
    try {
      const primaryLanguage = rootState.metadata?.contentLanguages.enabled[0];
      if (!primaryLanguage?.languageCode) return;

      const loadPromises = [catalogApi.getCatalogNames({ language: primaryLanguage.languageCode, catalogTypes: [0, 1] })];

      if (primaryLanguage?.fallbackLanguageCode) {
        loadPromises.push(catalogApi.getCatalogNames({ language: primaryLanguage.fallbackLanguageCode, catalogTypes: [0, 1] }));
      }

      const results = await Promise.all(loadPromises);
      const [catalogs] = results;

      let fallbackCatalogs: Catalog[] = [];
      if (primaryLanguage?.fallbackLanguageCode) {
        [, fallbackCatalogs] = results;
      }
      const allCatalogs = [...catalogs].map((catalog) => {
        const fallbackCatalog = fallbackCatalogs.find((entry) => entry.catalogId === catalog.catalogId);
        return {
          catalogId: catalog.catalogId,
          name: catalog.name,
          fallbackName: fallbackCatalog?.name
        };
      });
      commit("set-catalogs", allCatalogs);
    } catch (error) {
      emitter.emit("global-error", { type: "api", exception: error });
    }
  },
  createTranslationProject: async function ({ commit, state, rootState }, args: CreateTranslationProjectData) {
    if (!args.projectName || !args.sourceLanguageId || !args.targetLanguageId || !args.catalogId) {
      return;
    }

    const logger = eventLogger.beginContext("translationManager.createProject");
    try {
      commit("set-is-creating-project", true);
      commit("set-is-loading", true);
      logger.info("Starting creation of new translation project", { criteria: args }).alsoToConsole();
      const languages = rootState.metadata?.contentLanguages.available ?? [];

      const sourceLanguage = languages.find((lang) => args.sourceLanguageId === lang.languageId) ?? null;
      const targetLanguage = languages.find((lang) => args.targetLanguageId === lang.languageId) ?? null;

      if (!sourceLanguage || !targetLanguage) {
        return;
      }

      const isGroupEnabled = (groupType: TranslationGroupType) => {
        return args.groups.some((group) => group.type === groupType && group.enabled);
      };

      const createCriteria: TranslationProjectCreateCriteria = {
        name: args.projectName,
        catalogId: args.catalogId,
        sourceLanguage: sourceLanguage.languageCode,
        targetLanguage: targetLanguage.languageCode,
        showOnlyOutdatedValues: args.showOnlyOutdated,
        translationCategory: {
          loadCatalogGroups: isGroupEnabled(TranslationGroupType.CatalogGroupNames),
          loadAttributeValues: isGroupEnabled(TranslationGroupType.AttributeValues),
          loadCatalogGroupDescriptions: isGroupEnabled(TranslationGroupType.CatalogGroupDescriptions),
          loadAttributeUnits: isGroupEnabled(TranslationGroupType.AttributeUnits),
          loadCatalogGroupAttributes: isGroupEnabled(TranslationGroupType.CatalogGroupAttributes),
          loadGlobalAttributeValues: isGroupEnabled(TranslationGroupType.GlobalAttributeValues),
          loadVariationGroups: isGroupEnabled(TranslationGroupType.VariationGroups),
          loadGlobalAttributeUnits: isGroupEnabled(TranslationGroupType.GlobalAttributeUnits),
          loadArticleNames: isGroupEnabled(TranslationGroupType.ArticleNames),
          loadKeywords: isGroupEnabled(TranslationGroupType.Keywords),
          loadDescriptions: isGroupEnabled(TranslationGroupType.ArticleDescriptions)
        }
      };

      const project = await translationManagerApi.create(createCriteria);
      logger.info("Finished API creation request", { createdProject: project }).alsoToConsole();

      const translationProjectCatalog = state.catalogs.find((catalog) => catalog.catalogId === project.catalogId);
      if (!translationProjectCatalog) {
        throw new Error("Catalog with id " + project.catalogId + " is unknown");
      }

      const translationProject: TranslationProject = {
        translationProjectId: project.translationProjectId,
        name: project.name,
        catalog: translationProjectCatalog,
        sourceLanguage: sourceLanguage,
        targetLanguage: targetLanguage,
        createdOn: project.createdOn,
        createdBy: project.createdBy,
        updatedOn: project.updatedOn,
        updatedBy: project.updatedBy,
        groups: []
      };

      const translationProjects = [...state.translationProjects, translationProject];
      commit("set-translation-projects", translationProjects);
      commit("set-translation-project-hash", { project: translationProject, hash: hash(translationProject) });

      logger.info("Finished creation of translation project").alsoToConsole();
      return translationProject;
    } catch (error) {
      emitter.emit("global-error", { type: "api", exception: error });
      logger.error(error);
    } finally {
      logger.complete();
      commit("set-is-loading", false);
    }
  },
  applySearch: async function ({ commit, state }, args: TranslationGroupSearchParameters) {
    const translationGroup = state.translationGroups.find((group) => group.type === args.groupType);
    const searchParameterState: SearchParameterState = {
      currentPage: args.pageNumber,
      entriesPerPage: args.entriesPerPage,
      hasNextPage: false,
      hasPreviousPage: false,
      entryValueFilterType: args.entryValueFilterType,
      searchText: args.searchText,
      orderByColumn: args.orderByColumn,
      orderDescending: args.orderDirection
    };

    let entries: TranslationGroupEntry[] = [];

    if (translationGroup) {
      if (!args.skipLoadingIndicatorControl) {
        commit("set-translation-group-loading", { groupType: args.groupType, newValue: true });
      }
      try {
        const apiResult = await translationManagerApi.getTranslationEntries(args);

        searchParameterState.hasNextPage = apiResult.hasNextPage;
        searchParameterState.hasPreviousPage = args.pageNumber > 1;

        entries = apiResult.entries.map((entry) => {
          const hasDifferingValues = entry.hasDifferingValues ?? false;
          const targetText = hasDifferingValues ? "" : (entry.targetText ?? "");
          return {
            count: entry.count ?? 0,
            sourceText: entry.sourceText ?? "",
            targetText: targetText,
            originalTargetText: targetText,
            detailEntries: entry.detailEntries ?? [],
            hasDifferingValues: hasDifferingValues
          };
        });

        const groupHash = hash(entries);
        commit("set-translation-group-entries", { groupType: args.groupType, entries: entries });
        commit("set-translation-group-hash", { groupType: args.groupType, hash: groupHash });
        commit("set-translation-group-search-parameters", { groupType: args.groupType, searchParameters: searchParameterState });

        const result = {
          entries: entries,
          searchParameters: searchParameterState
        };

        return result;
      } catch (error) {
        emitter.emit("global-error", { type: "api", exception: error });
        return {
          entries: [],
          searchParameters: searchParameterState
        };
      } finally {
        if (!args.skipLoadingIndicatorControl) {
          commit("set-translation-group-loading", { groupType: args.groupType, newValue: false });
        }
      }
    }
  },
  loadRequiredTranslationGroups: async function (
    { state, commit, dispatch },
    args: {
      translationProjectId: string;
      executedAction?: () => Promise<unknown>;
      groupType?: TranslationGroupType;
    }
  ) {
    const groupTypesToTranslate = args.groupType ? [args.groupType] : state.translationGroups.map((group) => group.type);

    const setGroupLoadings = (value: boolean) => {
      for (const groupType of groupTypesToTranslate) {
        commit("set-translation-group-loading", { groupType: groupType, newValue: value });
      }
    };

    let result: unknown;
    try {
      if (!args.groupType) {
        commit("set-is-loading", true);
      }
      setGroupLoadings(true);

      if (args.executedAction) {
        result = await args.executedAction();
      }

      if (!args.groupType) {
        commit("set-is-loading", false);
      }

      const searchPromises = [];
      for (const groupType of groupTypesToTranslate) {
        const translationGroup = state.translationGroups.find((group) => group.type === groupType)!;

        const searchParameters: TranslationGroupSearchParameters = {
          translationProjectId: args.translationProjectId,
          groupType: translationGroup.type,
          searchText: null,
          entryValueFilterType: translationGroup.searchParameters?.entryValueFilterType ?? TranslationEntryValueFilterType.All,
          pageNumber: 1,
          entriesPerPage: translationGroup.searchParameters?.entriesPerPage ?? DEFAULT_PAGE_SIZE,
          skipLoadingIndicatorControl: true
        };

        const searchPromise = dispatch("applySearch", searchParameters);
        searchPromises.push(searchPromise);
      }

      await Promise.all(searchPromises);
    } finally {
      setGroupLoadings(false);
    }

    return result;
  },
  translateEntriesWithDeepL: async function (
    { dispatch },
    args: {
      translationProjectId: string;
      groupType?: TranslationGroupType;
      onlyEmpty: boolean;
    }
  ) {
    try {
      const translate = async () => await translationManagerApi.translateEntriesWithDeepL(args.translationProjectId, args.onlyEmpty, args.groupType);

      await dispatch("loadRequiredTranslationGroups", {
        translationProjectId: args.translationProjectId,
        groupType: args.groupType,
        executedAction: translate
      });
    } catch (error) {
      emitter.emit("global-error", { type: "api", exception: error });
    }
  },
  translateSelectedEntriesWithDeepL: async function (
    { state, commit },
    args: {
      groupType: TranslationGroupType;
      entriesToTranslate: TranslationGroupEntry[];
    }
  ) {
    const translationGroup = state.translationGroups.find((group) => group.type === args.groupType);

    if (!translationGroup || !state.currentTranslationProject?.sourceLanguage || !state.currentTranslationProject?.targetLanguage) {
      return;
    }

    try {
      const sourceLanguageCode = state.currentTranslationProject.sourceLanguage.languageCode;
      const targetLanguageCode = state.currentTranslationProject.targetLanguage.languageCode;
      const sourceTexts = _.chain(args.entriesToTranslate)
        .map((entry) => entry.sourceText)
        .uniq()
        .value();
      const translatedTexts = await translationApi.getTranslatedText(sourceTexts, sourceLanguageCode, targetLanguageCode);
      const sourceToTranslated = _.zipObject(sourceTexts, translatedTexts);
      const mappedEntries = translationGroup.entries.map((entry) => {
        if (sourceToTranslated[entry.sourceText]) {
          entry.targetText = sourceToTranslated[entry.sourceText] as string;
        }

        return entry;
      });

      commit("set-translation-group-entries", { groupType: args.groupType, entries: mappedEntries });
    } catch (error) {
      emitter.emit("global-error", { type: "api", exception: error });
    }
  },
  exportTranslations: async function (
    { state, rootState },
    args: { fileType: TranslationFileType; translationGroup?: TranslationGroupType | null; progressHandler: ProgressHandler }
  ): Promise<{ filename: string } | null> {
    const progressHandle = args.progressHandler;
    progressHandle
      .init(
        $t("translationManager.exportDialogTitle"),
        $t(args.translationGroup ? "translationManager.exportDialogMessageTable" : "translationManager.exportDialogMessageProject")
      )
      .start()
      .open();
    const translationProjectId = state.currentTranslationProject!.translationProjectId;
    try {
      const response = await translationManagerApi.exportTranslations(args.fileType, translationProjectId, progressHandle.abortSignal, args.translationGroup);
      const translationProjectName = state.currentTranslationProject!.name;
      const fileEnding = args.fileType === TranslationFileType.XLIFF ? "xliff" : "xlsx";
      const filename = encodeURIComponent(`${translationProjectName}.${fileEnding}`);

      const url = buildDownloadUrl(rootState.meta.config.apiUrl, endpoints.translationManager.projects.download, rootState.auth.token, {
        filename: filename,
        token: response
      });

      safeDownload(url);
      return { filename };
    } catch (error: any) {
      if (!(error instanceof CanceledError) || error.code !== "ERR_CANCELED") {
        emitter.emit("global-error", { type: "api", exception: error });
      }

      return null;
    } finally {
      progressHandle.finish();
    }
  },
  importTranslations: async function (
    { state, dispatch },
    args: { fileType: TranslationFileType; fileToImport: File; translationGroup?: TranslationGroupType | null; progressHandler: ProgressHandler }
  ) {
    const progressHandle = args.progressHandler;
    const translationProjectId = state.currentTranslationProject!.translationProjectId;
    progressHandle
      .init(
        $t("translationManager.importDialogTitle"),
        $t(args.translationGroup ? "translationManager.importDialogMessageTable" : "translationManager.importDialogMessageProject")
      )
      .start()
      .open();
    try {
      await translationManagerApi.importTranslations(args.fileType, translationProjectId, args.fileToImport, progressHandle.abortSignal, args.translationGroup);
      progressHandle.finish();
      await dispatch("loadRequiredTranslationGroups", {
        translationProjectId: translationProjectId,
        groupType: args.translationGroup
      });
    } catch (error: any) {
      if (!(error instanceof CanceledError) || error.code !== "ERR_CANCELED") {
        let errorMessage = "";
        if (error?.isAxiosError) {
          const status = error.response?.status;
          const fileErrors = error.response?.data?.file;

          if (status === 400 && Array.isArray(fileErrors) && fileErrors[0]) {
            errorMessage = $t("api.errors." + fileErrors[0]);
          }

          if (!errorMessage) {
            errorMessage = $t("UploadFailed");
          }
        } else {
          errorMessage = $t("UploadFailed");
        }

        if (!errorMessage) emitter.emit("global-error", { type: "api", exception: error });
        else throw new Error(errorMessage);
      }
    } finally {
      progressHandle.finish();
    }
  },
  publishTranslationProject: async function ({ state }, args: { progressHandler: ProgressHandler }) {
    if (!state.currentTranslationProject?.translationProjectId) {
      return;
    }

    const progressHandle = args.progressHandler;
    progressHandle.init($t("translationManager.publishDialogTitle"), $t("translationManager.publishDialogText")).start().open();
    try {
      await translationManagerApi.publishTranslationProject(state.currentTranslationProject.translationProjectId, progressHandle.abortSignal);
      progressHandle.finish();
    } catch (error: any) {
      if (!(error instanceof CanceledError) || error.code !== "ERR_CANCELED") {
        emitter.emit("global-error", { type: "api", exception: error });
      }
    } finally {
      progressHandle.finish();
    }
  },
  reloadOverviewValues: async function ({ state, commit }) {
    if (!state.currentTranslationProject?.translationProjectId) {
      return;
    }

    try {
      commit("set-is-overview-loading", true);
      const overviewValuesFromApi = await translationManagerApi.getOverviewValues(state.currentTranslationProject.translationProjectId);
      const overviewValues: TranslationManagerOverviewValues = {
        textCount: overviewValuesFromApi.textCount ?? 0,
        emptyTextCount: overviewValuesFromApi.emptyTextCount ?? 0,
        translatedTextCount: overviewValuesFromApi.translatedTextCount ?? 0,
        wordCount: overviewValuesFromApi.wordCount ?? 0,
        emptyWordCount: overviewValuesFromApi.emptyWordCount ?? 0,
        translatedWordCount: overviewValuesFromApi.translatedWordCount ?? 0,
        characterCount: overviewValuesFromApi.characterCount ?? 0,
        emptyCharacterCount: overviewValuesFromApi.emptyCharacterCount ?? 0,
        translatedCharacterCount: overviewValuesFromApi.translatedCharacterCount ?? 0
      };
      commit("set-overview-values", overviewValues);
    } catch (error: any) {
      emitter.emit("global-error", { type: "api", exception: error });
    } finally {
      commit("set-is-overview-loading", false);
    }
  }
};

const storeModule = {
  namespaced: true,
  state: { ...defaultState() },
  getters,
  mutations,
  actions
};
export default storeModule;
